"use client";

import { useCallback } from "react";
import { appDB } from "@/indexedDB";
import { useBlockActions } from "@/contexts/blockContext";
import type { Block } from "@/types/block";

/**
 * Re-stamp `pageType` / `workareaId` on a moved page's DESCENDANTS to mirror what
 * the backend does recursively (BlockService.updateSubtreeACL on drag-and-drop).
 *
 * WHY THIS EXISTS: the drag-drop API response does NOT return the re-stamped
 * descendants — it only carries `updatedAt` / `stampedBlocks` / `failedBlockIds`.
 * The backend has already updated every descendant in Mongo, but the client's
 * context + IndexedDB still hold the old `pageType` / `workareaId` for those
 * descendants until a reload. Every drag-drop entry point (sidebar + all three
 * editors) reproduces the backend's rule locally so the subtree is correct
 * immediately, with no reload.
 *
 * RULE — MUST stay in lockstep with backend `updateSubtreeACL`:
 *   • workareaId          → set on EVERY descendant (page + content blocks)
 *   • pageType/isTemplate → only on real page blocks (blockType === "page").
 *     Stamping content / collection blocks makes `inferBlockType` misclassify
 *     them as pages on history restore, so they get only their workareaId.
 *
 * This is the SINGLE source of truth for that mirroring — all drag-drop callers
 * share it, so keeping it aligned with the backend is a one-file change.
 *
 * Cost: one subtree walk (context reads + per-node IDB point lookups) + one
 * `upsertBlocks` (single re-render) + one IDB `bulkUpdate` (single
 * transaction). No always-on subscription, no extra network.
 */
export function useRestampMovedSubtree(workspaceId: string) {
  const { getChildrenBlocks: getChildrenFromContext, upsertBlocks } = useBlockActions();
  // Retained for call-site compatibility; the IDB reads below are on-demand
  // point lookups keyed by block id, so the workspace scope is implicit.
  void workspaceId;

  return useCallback(
    async (
      rootId: string,
      newPageType: string | undefined,
      newWorkareaId: string | null,
    ) => {
      // Collect all descendants (root excluded — the caller already updated it).
      const descendants: Block[] = [];
      const seen = new Set<string>([rootId]);
      const stack: string[] = [rootId];
      while (stack.length > 0) {
        const currentId = stack.pop()!;
        // Union context + IDB children so deep / unloaded nodes aren't missed.
        // IDB side is an on-demand point read (parent record → bulkGet its
        // blockIds) — replaces the old always-on whole-workspace live query,
        // which made every caller of this hook a full-workspace subscriber.
        const byId = new Map<string, Block>();
        for (const k of getChildrenFromContext(currentId) || []) byId.set(k._id, k);
        try {
          const idbParent = await appDB.blocks.get(currentId);
          if (idbParent?.blockIds?.length) {
            const records = await appDB.blocks.bulkGet(idbParent.blockIds);
            for (const r of records) {
              if (!r || byId.has(r._id)) continue;
              const { isSync, localUpdatedAt, syncAttempts, syncError, ...blockData } = r;
              byId.set(r._id, blockData as Block);
            }
          }
        } catch {
          // IDB unavailable — context children still cover the loaded subtree.
        }
        for (const child of byId.values()) {
          if (seen.has(child._id) || child.status === "deleted") continue;
          seen.add(child._id);
          descendants.push(child);
          stack.push(child._id);
        }
      }
      if (descendants.length === 0) return;

      const mergedBlocks: Block[] = [];
      const idbChanges: { key: string; changes: Record<string, any> }[] = [];
      for (const b of descendants) {
        const ctxChanges: Partial<Block> = {};
        // IDB uses dotted keypaths, not a whole-`value` replace, so a concurrent
        // content write to this descendant isn't clobbered.
        const idbChange: Record<string, any> = {};

        // Write only fields that changed (mirrors the backend's skip-when-unchanged).
        if (b.workareaId !== newWorkareaId) {
          ctxChanges.workareaId = newWorkareaId;
          idbChange.workareaId = newWorkareaId;
        }
        if (
          newPageType &&
          b.blockType === "page" &&
          (b.value as any)?.pageType !== newPageType
        ) {
          ctxChanges.value = {
            ...(b.value as any),
            pageType: newPageType,
            isTemplate: newPageType === "template",
          } as any;
          idbChange["value.pageType"] = newPageType;
          idbChange["value.isTemplate"] = newPageType === "template";
        }

        if (Object.keys(idbChange).length === 0) continue;
        mergedBlocks.push({ ...b, ...ctxChanges });
        idbChanges.push({ key: b._id, changes: idbChange });
      }

      if (idbChanges.length === 0) return;
      upsertBlocks(mergedBlocks);                // context: single synchronous re-render
      await appDB.blocks.bulkUpdate(idbChanges); // IDB: single transaction
    },
    [getChildrenFromContext, upsertBlocks],
  );
}
